import '@/i18n';

import { Toaster as Sonner } from "@/components/ui/sonner";
import { toast } from "sonner";
import { createPushDedupeStore, shouldHandlePush as shouldHandlePushEvent, pushKey, pushToastId } from "@/lib/push-dedupe";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useParams, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { ThemeProvider } from "next-themes";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { usePatientRole } from "@/hooks/usePatientRole";
import { ShiftProvider } from "@/contexts/ShiftContext";
import { FocusedPatientProvider } from "@/contexts/FocusedPatientContext";
import { SubscriptionGuard } from "@/components/subscription/SubscriptionGuard";
import { Capacitor } from "@capacitor/core";
import { trackScreen } from './lib/posthog';
import { useLocation } from 'react-router-dom';
import AnalyticsContextBridge from '@/components/analytics/AnalyticsContextBridge';
import { useReferralCapture } from '@/hooks/useReferralCapture';
import { TaskAlarmScheduler } from '@/components/caregiver/TaskAlarmScheduler';
import { useAppVersionCheck } from '@/hooks/useAppVersionCheck';
import { AppUpdateDialog } from '@/components/AppUpdateDialog';
import { useState } from 'react';


import LandingPage from "./pages/Landing";
import AuthPage from "./pages/Auth";
import OAuthConsentPage from "./pages/OAuthConsent";
import VerifyOtpPage from "./pages/VerifyOtp";
import HomePage from "./pages/Home";
import PatientsListPage from "./pages/patients/PatientsList";
import NewPatientPage from "./pages/patients/NewPatient";
import EditPatientPage from "./pages/patients/EditPatient";
import PatientDetailPage from "./pages/patients/PatientDetail";

import CarePlanPage from "./pages/care-plan/CarePlan";
import AddTaskPage from "./pages/care-plan/AddTask";
import AIWizardPage from "./pages/care-plan/AIWizard";
import CaregiverAgendaPage from "./pages/caregiver/CaregiverAgenda";
import ExecuteTaskPage from "./pages/caregiver/ExecuteTask";
import HandoffPage from "./pages/caregiver/Handoff";
import SporadicMedicationPage from "./pages/caregiver/SporadicMedication";
import AdHocTaskPage from "./pages/caregiver/AdHocTask";
import RegisterIncidentPage from "./pages/caregiver/RegisterIncident";
import FamilyDashboardPage from "./pages/family/FamilyDashboard";
import ShiftDetailPage from "./pages/family/ShiftDetail";
import DailyReportPage from "./pages/family/DailyReport";
import WeeklyReportPage from "./pages/family/WeeklyReport";
import StandaloneReportPage from "./pages/family/StandaloneReportPage";
import InventoryListPage from "./pages/inventory/InventoryList";
import VitalRangesListPage from "./pages/vital-ranges/VitalRangesList";
import AddMedicationPage from "./pages/inventory/AddMedication";
import MedicationDetailPage from "./pages/inventory/MedicationDetail";
import AcceptInvitePage from "./pages/AcceptInvite";
import ProfilePage from "./pages/Profile";
import NotificationsListPage from "./pages/notifications/NotificationsList";
import NotificationDetailPage from "./pages/notifications/NotificationDetail";
import NotificationSettingsPage from "./pages/settings/NotificationSettings";
import SystemSettingsPage from "./pages/settings/SystemSettings";
import ShiftCheckInPage from "./pages/shifts/ShiftCheckInPage";
import ShiftSwapPage from "./pages/shifts/ShiftSwapPage";
import ShiftTemplateFormPage from "./pages/shift-templates/ShiftTemplateFormPage";
import SubscriptionPage from "./pages/subscription/SubscriptionPage";
import CompanyDashboard from "./pages/company/CompanyDashboard";
import CreateCompany from "./pages/company/CreateCompany";
import CompanySettings from "./pages/company/CompanySettings";
import AcceptCompanyInvitePage from "./pages/company/AcceptCompanyInvite";
import CaregiverDetailPage from "./pages/company/CaregiverDetailPage";
import NotFound from "./pages/NotFound";
import WelcomePage from "./pages/Welcome";
import HelpPage from "./pages/Help";
import ResetPasswordPage from "./pages/ResetPassword";
import LegalDocPage from "./pages/legal/LegalDocPage";
import PrivacyCenterPage from "./pages/privacy/PrivacyCenter";
import DPODashboardPage from "./pages/dpo/DPODashboard";
import AdminDashboardPage from "./pages/admin/AdminDashboard";
import AdminReferralsPage from "./pages/admin/AdminReferralsPage";
import AdminStripeConfigPage from "./pages/admin/AdminStripeConfig";
import AdminStripeMigrationPage from "./pages/admin/AdminStripeMigration";
import AdminCronJobsPage from "./pages/admin/AdminCronJobs";
import AdminUsersPage from "./pages/admin/AdminUsers";
import AdminUserDetailPage from "./pages/admin/AdminUserDetail";

import PartnersLandingPage from "./pages/partners/PartnersLanding";
import PartnerSignupPage from "./pages/partners/PartnerSignup";
import PartnerPanelPage from "./pages/partners/PartnerPanel";
import { CookieBanner } from "@/components/legal/CookieBanner";
import { MedicalDisclaimerModal } from "@/components/legal/MedicalDisclaimerModal";
import ScrollToTop from "@/components/ScrollToTop";
import { queryClient } from '@/lib/query-client';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-primary">Carregando...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  return <>{children}</>;
}

function CaregiverRoute({ children }: { children: React.ReactNode }) {
  const params = useParams<{ id?: string; patientId?: string }>();
  const id = params.id || params.patientId;
  const { isLoading, isCaregiver } = usePatientRole(id);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-primary">Carregando...</div>
      </div>
    );
  }

  if (!isCaregiver) {
    return <Navigate to="/app" replace />;
  }

  return <>{children}</>;
}

function FamilyRoute({ children }: { children: React.ReactNode }) {
  const { patientId } = useParams<{ patientId: string }>();
  const { isLoading, canViewDashboard } = usePatientRole(patientId);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-primary">Carregando...</div>
      </div>
    );
  }

  if (!canViewDashboard) {
    return <Navigate to="/app" replace />;
  }

  return <>{children}</>;
}

/**
 * Checks app version against Supabase system_settings and shows an update dialog
 * when the installed version is outdated. Runs only on native platforms.
 */
function AppVersionGuard() {
  const { updateRequired, updateRecommended, storeUrl, dismissRecommended } = useAppVersionCheck();
  const [open, setOpen] = useState(true);

  const handleUpdate = async () => {
    try {
      const { Browser } = await import('@capacitor/browser');
      await Browser.open({ url: storeUrl });
    } catch {
      // Fallback: open in system browser
      window.open(storeUrl, '_blank');
    }
    // Keep mandatory dialog open after returning from store
  };

  const handleDismiss = () => {
    dismissRecommended();
    setOpen(false);
  };

  if (!updateRequired && !updateRecommended) return null;

  return (
    <AppUpdateDialog
      open={updateRequired || (updateRecommended && open)}
      mandatory={updateRequired}
      onUpdate={handleUpdate}
      onDismiss={handleDismiss}
    />
  );
}

function AppRoutes() {
  useReferralCapture();
  return (
    <Routes>
      <Route path="/" element={Capacitor.isNativePlatform() ? <Navigate to="/app" replace /> : <LandingPage />} />
      <Route path="/auth" element={<AuthPage />} />

      {/* MCP / OAuth 2.1 consent screen */}
      <Route path="/.lovable/oauth/consent" element={<OAuthConsentPage />} />
      <Route path="/verify-otp" element={<VerifyOtpPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/invite/company/:token" element={<AcceptCompanyInvitePage />} />
      <Route path="/invite/:token" element={<AcceptInvitePage />} />

      {/* Public legal documents */}
      <Route path="/legal/:slug" element={<LegalDocPage />} />


      {/* OAuth callback route for Google Auth deep link */}
      <Route path="/google-auth" element={<AuthPage />} />

      {/* Subscription Page - Protected but NOT subscription-gated */}
      <Route
        path="/app/subscription"
        element={
          <ProtectedRoute>
            <SubscriptionPage />
          </ProtectedRoute>
        }
      />

      {/* Protected App Routes - Subscription Required */}
      <Route
        path="/app/welcome"
        element={
          <ProtectedRoute>
            <WelcomePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/app"
        element={
          <ProtectedRoute>
            <SubscriptionGuard>
              <HomePage />
            </SubscriptionGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/patients"
        element={
          <ProtectedRoute>
            <SubscriptionGuard>
              <PatientsListPage />
            </SubscriptionGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/patients/new"
        element={
          <ProtectedRoute>
            <SubscriptionGuard>
              <NewPatientPage />
            </SubscriptionGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/patients/:id"
        element={
          <ProtectedRoute>
            <SubscriptionGuard>
              <PatientDetailPage />
            </SubscriptionGuard>
          </ProtectedRoute>
        }
      />

      <Route
        path="/app/patients/:id/edit"
        element={
          <ProtectedRoute>
            <SubscriptionGuard>
              <EditPatientPage />
            </SubscriptionGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/patients/:id/care-plan"
        element={
          <ProtectedRoute>
            <SubscriptionGuard>
              <CarePlanPage />
            </SubscriptionGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/patients/:id/care-plan/add"
        element={
          <ProtectedRoute>
            <SubscriptionGuard>
              <AddTaskPage />
            </SubscriptionGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/patients/:id/care-plan/edit/:taskId"
        element={
          <ProtectedRoute>
            <SubscriptionGuard>
              <AddTaskPage />
            </SubscriptionGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/patients/:id/care-plan/ai-wizard"
        element={
          <ProtectedRoute>
            <SubscriptionGuard>
              <AIWizardPage />
            </SubscriptionGuard>
          </ProtectedRoute>
        }
      />

      {/* Caregiver Routes */}
      <Route
        path="/app/caregiver/:id"
        element={
          <ProtectedRoute>
            <SubscriptionGuard>
              <CaregiverRoute>
                <CaregiverAgendaPage />
              </CaregiverRoute>
            </SubscriptionGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/caregiver/:id/execute/:taskId"
        element={
          <ProtectedRoute>
            <SubscriptionGuard>
              <CaregiverRoute>
                <ExecuteTaskPage />
              </CaregiverRoute>
            </SubscriptionGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/caregiver/:id/handoff"
        element={
          <ProtectedRoute>
            <SubscriptionGuard>
              <CaregiverRoute>
                <HandoffPage />
              </CaregiverRoute>
            </SubscriptionGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/caregiver/:id/sporadic"
        element={
          <ProtectedRoute>
            <SubscriptionGuard>
              <CaregiverRoute>
                <SporadicMedicationPage />
              </CaregiverRoute>
            </SubscriptionGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/caregiver/:id/adhoc"
        element={
          <ProtectedRoute>
            <SubscriptionGuard>
              <CaregiverRoute>
                <AdHocTaskPage />
              </CaregiverRoute>
            </SubscriptionGuard>
          </ProtectedRoute>
        }
      />

      <Route
        path="/app/caregiver/:id/incident"
        element={
          <ProtectedRoute>
            <SubscriptionGuard>
              <CaregiverRoute>
                <RegisterIncidentPage />
              </CaregiverRoute>
            </SubscriptionGuard>
          </ProtectedRoute>
        }
      />

      {/* Shifts Standardization Routes */}
      <Route
        path="/app/patients/:patientId/check-in"
        element={
          <ProtectedRoute>
            <SubscriptionGuard>
              <CaregiverRoute>
                <ShiftCheckInPage />
              </CaregiverRoute>
            </SubscriptionGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/patients/:patientId/shift-swap"
        element={
          <ProtectedRoute>
            <SubscriptionGuard>
              <CaregiverRoute>
                <ShiftSwapPage />
              </CaregiverRoute>
            </SubscriptionGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/patients/:patientId/templates/new"
        element={
          <ProtectedRoute>
            <SubscriptionGuard>
              <ShiftTemplateFormPage />
            </SubscriptionGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/patients/:patientId/templates/:templateId"
        element={
          <ProtectedRoute>
            <SubscriptionGuard>
              <ShiftTemplateFormPage />
            </SubscriptionGuard>
          </ProtectedRoute>
        }
      />

      {/* Family Dashboard Routes */}
      <Route
        path="/app/family/:patientId"
        element={
          <ProtectedRoute>
            <SubscriptionGuard>
              <FamilyRoute>
                <FamilyDashboardPage />
              </FamilyRoute>
            </SubscriptionGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/family/:patientId/shift/:shiftId"
        element={
          <ProtectedRoute>
            <SubscriptionGuard>
              <FamilyRoute>
                <ShiftDetailPage />
              </FamilyRoute>
            </SubscriptionGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/family/:patientId/report/daily"
        element={
          <ProtectedRoute>
            <SubscriptionGuard>
              <FamilyRoute>
                <DailyReportPage />
              </FamilyRoute>
            </SubscriptionGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/family/:patientId/report/weekly"
        element={
          <ProtectedRoute>
            <SubscriptionGuard>
              <FamilyRoute>
                <WeeklyReportPage />
              </FamilyRoute>
            </SubscriptionGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/family/:patientId/report/medical-30-days"
        element={
          <ProtectedRoute>
            <SubscriptionGuard>
              <FamilyRoute>
                <StandaloneReportPage />
              </FamilyRoute>
            </SubscriptionGuard>
          </ProtectedRoute>
        }
      />

      {/* Inventory Routes */}
      <Route
        path="/app/inventory/:patientId"
        element={
          <ProtectedRoute>
            <SubscriptionGuard>
              <InventoryListPage />
            </SubscriptionGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/inventory/:patientId/add"
        element={
          <ProtectedRoute>
            <SubscriptionGuard>
              <AddMedicationPage />
            </SubscriptionGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/inventory/:patientId/item/:itemId"
        element={
          <ProtectedRoute>
            <SubscriptionGuard>
              <MedicationDetailPage />
            </SubscriptionGuard>
          </ProtectedRoute>
        }
      />

      {/* Vital Ranges Route */}
      <Route
        path="/app/vital-ranges/:patientId"
        element={
          <ProtectedRoute>
            <SubscriptionGuard>
              <VitalRangesListPage />
            </SubscriptionGuard>
          </ProtectedRoute>
        }
      />

      {/* Profile Route - No subscription required */}
      <Route
        path="/app/profile"
        element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/privacy"
        element={
          <ProtectedRoute>
            <PrivacyCenterPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/dpo"
        element={
          <ProtectedRoute>
            <DPODashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/admin"
        element={
          <ProtectedRoute>
            <AdminDashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/admin/referrals"
        element={
          <ProtectedRoute>
            <AdminReferralsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/admin/stripe"
        element={
          <ProtectedRoute>
            <AdminStripeConfigPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/admin/stripe/migration"
        element={
          <ProtectedRoute>
            <AdminStripeMigrationPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/admin/cron"
        element={
          <ProtectedRoute>
            <AdminCronJobsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/admin/users"
        element={
          <ProtectedRoute>
            <AdminUsersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/admin/users/:userId"
        element={
          <ProtectedRoute>
            <AdminUserDetailPage />
          </ProtectedRoute>
        }
      />


      {/* Programa de parcerias / indicações - rotas públicas */}
      <Route path="/parceiros" element={<PartnersLandingPage />} />
      <Route path="/parceiros/cadastro" element={<PartnerSignupPage />} />
      <Route
        path="/parceiros/painel"
        element={
          <ProtectedRoute>
            <PartnerPanelPage />
          </ProtectedRoute>
        }
      />

      {/* Help Center - No subscription required */}
      <Route
        path="/app/help"
        element={
          <ProtectedRoute>
            <HelpPage />
          </ProtectedRoute>
        }
      />

      {/* Notifications Routes - No subscription required */}
      <Route
        path="/app/notifications"
        element={
          <ProtectedRoute>
            <NotificationsListPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/notifications/:id"
        element={
          <ProtectedRoute>
            <NotificationDetailPage />
          </ProtectedRoute>
        }
      />

      {/* Settings Routes - Notification settings accessible without subscription */}
      <Route
        path="/app/settings/notifications"
        element={
          <ProtectedRoute>
            <NotificationSettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/settings/system"
        element={
          <ProtectedRoute>
            <SubscriptionGuard>
              <SystemSettingsPage />
            </SubscriptionGuard>
          </ProtectedRoute>
        }
      />

      {/* Company routes */}
      <Route
        path="/app/company"
        element={
          <ProtectedRoute>
            <CompanyDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/company/new"
        element={
          <ProtectedRoute>
            <CreateCompany />
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/company/settings"
        element={
          <ProtectedRoute>
            <CompanySettings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/app/company/caregiver/:userId"
        element={
          <ProtectedRoute>
            <CaregiverDetailPage />
          </ProtectedRoute>
        }
      />

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

// Module-level guards so remounts never register duplicate push listeners
// (duplicate listeners were the cause of repeated toasts for one notification).
let pushListenersSetup = false;
const pushDedupeStore = createPushDedupeStore();

/** Returns false when the same push was already handled in the dedupe window. */
const shouldHandlePush = (key: string) => shouldHandlePushEvent(pushDedupeStore, key);

const PushNotificationHandler = () => {
  const navigate = useNavigate();

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    const setupListeners = async () => {
      if (pushListenersSetup) return;
      pushListenersSetup = true;


      const { PushNotifications } = await import('@capacitor/push-notifications');
      const { LocalNotifications } = await import('@capacitor/local-notifications');
      // Nota: não usamos @capacitor-firebase/messaging para exibir/receber pushes
      // aqui — apenas o plugin @capacitor/push-notifications, para evitar
      // notificações duplicadas na bandeja.


      // Request local notification permission (idempotent)
      try {
        await LocalNotifications.requestPermissions();
      } catch (e) {
        console.log('[Push] LocalNotifications permission request skipped:', e);
      }

      // Create notification channel for Android 8.0+
      try {
        await PushNotifications.createChannel({
          id: 'default',
          name: 'Default Notifications',
          description: 'General app notifications',
          importance: 5, // HIGH
          visibility: 1, // PUBLIC
          sound: 'default',
          vibration: true,
        });
        console.log('[Push] Notification channel created');
      } catch (e) {
        console.log('[Push] Channel creation skipped (may already exist):', e);
      }

      // IMPORTANTE (regressão: notificações duplicadas na bandeja)
      // Um único caminho de exibição: a notificação enviada pelo sistema (bloco
      // `notification` do FCM). Não registramos listeners do plugin
      // @capacitor-firebase/messaging aqui nem agendamos notificação local para
      // pushes — isso gerava duas entradas na bandeja para o mesmo aviso.

      // Action Listener (Tap on notification - background/closed)
      await PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
        console.log('[Push] Action performed', { url: (notification.notification?.data as Record<string, any> | undefined)?.url });

        // Try multiple locations for the URL (FCM sends data in different places)
        const notificationData = notification.notification?.data as Record<string, any> | undefined;
        const url = notificationData?.url || '/app';

        console.log('[Push] Navigating to:', url);

        // Use setTimeout to ensure navigation happens after app is fully ready
        setTimeout(() => {
          navigate(url);
        }, 100);
      });

      // Received Listener (Foreground) — apenas toast dentro do app.
      // A notificação do sistema já foi (ou será) exibida pelo próprio Android/iOS.
      await PushNotifications.addListener('pushNotificationReceived', (notification) => {
        const data = (notification.data || {}) as Record<string, any>;
        const title = notification.title || data.title || 'Nova notificação';
        const body = notification.body || data.body || '';
        const key = pushKey({ notificationId: data.notificationId, title, body });

        console.log('[Push] Notification received in foreground', { key });
        if (!shouldHandlePush(key)) return;

        toast.info(title, {
          id: pushToastId(key),
          description: body,
          duration: 8000,
        });
      });


      // Tap on the locally-scheduled foreground notification
      await LocalNotifications.addListener('localNotificationActionPerformed', (notification) => {
        const extra = (notification.notification?.extra || {}) as Record<string, any>;
        // Task alarms: go straight to the caregiver agenda highlighting the task.
        if (extra.careosTaskAlarm) {
          const url = extra.patientId
            ? `/app/caregiver/${extra.patientId}?highlight=${extra.instanceId}`
            : '/app';
          setTimeout(() => navigate(url), 100);
          return;
        }
        console.log('[LocalNotif] Action performed', { url: extra.url });
        const url = extra.url || '/app';
        setTimeout(() => {
          navigate(url);
        }, 100);
      });


      console.log('[Push] Listeners setup complete');
    };

    setupListeners();

    // Cleanup: Remove ONLY our navigation listeners, not the registration ones
    return () => {
      // Don't remove listeners on unmount to prevent race conditions
      // The listeners are safe to keep as they just navigate
    };
  }, [navigate]);

  // Deep Linking Handler - Including OAuth callback processing
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    import('@capacitor/app').then(({ App }) => {
      const urlListener = App.addListener('appUrlOpen', async (event) => {
        console.log('[DeepLink] App opened with URL:', event.url);

        try {
          // Handle careos:// scheme
          if (event.url.startsWith('careos://')) {
            console.log('[DeepLink] careos:// scheme detected');

            // Try to parse as URL to check for OAuth tokens
            try {
              const parsedUrl = new URL(event.url.replace('careos://', 'https://placeholder.local/'));
              
              // 1. Check for PKCE Code
              const code = parsedUrl.searchParams.get('code');
              if (code) {
                console.log('[DeepLink] OAuth PKCE code detected, exchanging for session...');
                const { supabase } = await import('@/integrations/supabase/client');

                // Close the browser before establishing session
                try {
                  const { Browser } = await import('@capacitor/browser');
                  await Browser.close();
                } catch (e) { /* ignore */ }

                const { error: exchangeError } = await supabase.auth.exchangeCodeForSession(code);

                if (exchangeError) {
                  console.error('[DeepLink] Failed to exchange OAuth code:', exchangeError);
                  setTimeout(() => navigate('/auth'), 100);
                } else {
                  console.log('[DeepLink] OAuth session established successfully');
                  setTimeout(() => navigate('/app'), 100);
                }
                return;
              }

              // 2. Check for Implicit Flow (tokens in hash)
              const hash = parsedUrl.hash;
              if (hash && hash.includes('access_token=')) {
                console.log('[DeepLink] OAuth tokens detected in hash, setting session...');
                const hashParams = new URLSearchParams(hash.substring(1));
                const accessToken = hashParams.get('access_token');
                const refreshToken = hashParams.get('refresh_token');

                if (accessToken && refreshToken) {
                  const { supabase } = await import('@/integrations/supabase/client');
                  
                  // Close the browser before establishing session
                  try {
                    const { Browser } = await import('@capacitor/browser');
                    await Browser.close();
                  } catch (e) { /* ignore */ }

                  const { error: sessionError } = await supabase.auth.setSession({
                    access_token: accessToken,
                    refresh_token: refreshToken
                  });

                  if (sessionError) {
                    console.error('[DeepLink] Failed to set OAuth session:', sessionError);
                    setTimeout(() => navigate('/auth'), 100);
                  } else {
                    console.log('[DeepLink] OAuth session set successfully');
                    setTimeout(() => navigate('/app'), 100);
                  }
                  return;
                }
              }

            } catch (parseErr) {
              console.warn('[DeepLink] Could not parse careos:// URL as URL object:', parseErr);
            }

            // Regular careos:// deep link (e.g. careos://app/patients)
            const internalPath = event.url.replace('careos://', '/');
            // Prevent navigating to raw auth fragments if they leaked through
            if (internalPath.includes('#access_token=')) return;

            console.log('[DeepLink] Custom scheme, navigating to:', internalPath);
            setTimeout(() => navigate(internalPath), 100);
            return;
          }

          const url = new URL(event.url);

          // Check if this is an OAuth callback (contains access_token in hash or google-auth path)
          const isOAuthCallback = url.pathname.includes('google-auth') ||
            url.hash.includes('access_token') ||
            url.searchParams.has('access_token');

          if (isOAuthCallback) {
            console.log('[DeepLink] OAuth callback detected, processing tokens...');

            // Extract tokens from hash fragment (Supabase returns them in hash)
            const hashParams = new URLSearchParams(url.hash.substring(1));
            const accessToken = hashParams.get('access_token');
            const refreshToken = hashParams.get('refresh_token');

            if (accessToken && refreshToken) {
              console.log('[DeepLink] Found tokens in hash, setting session...');
              const { supabase } = await import('@/integrations/supabase/client');
              const { data, error } = await supabase.auth.setSession({
                access_token: accessToken,
                refresh_token: refreshToken
              });

              if (error) {
                console.error('[DeepLink] Failed to set session:', error);
                setTimeout(() => navigate('/auth'), 100);
              } else {
                console.log('[DeepLink] Session set successfully');
                setTimeout(() => navigate('/app'), 100);
              }
            }
            return;
          }

          // Regular deep link (e.g. https://careos.life/app/...)
          const path = url.pathname;
          console.log('[DeepLink] Regular deep link, navigating to:', path);
          setTimeout(() => navigate(path), 100);
        } catch (error) {
          console.error('[DeepLink] Failed to process URL:', error);
        }
      });

      return () => {
        urlListener.then(l => l.remove());
      };
    });
  }, [navigate]);

  return null;
};

const PostHogTracker = () => {
  const location = useLocation();

  useEffect(() => {
    // Track page views manually when route changes
    trackScreen(location.pathname);
  }, [location]);

  return null;
};


const App = () => {

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
        <TooltipProvider>
          <Sonner />
          <BrowserRouter>
            <PostHogTracker />
            <ScrollToTop />
            <AuthProvider>
              <AnalyticsContextBridge />
              <FocusedPatientProvider>
                <ShiftProvider>
                  <PushNotificationHandler />
                  <TaskAlarmScheduler />
                  <AppVersionGuard />
                  <AppRoutes />
                  <CookieBanner />
                  <MedicalDisclaimerModal />
                </ShiftProvider>
              </FocusedPatientProvider>
            </AuthProvider>
          </BrowserRouter>
        </TooltipProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
};

export default App;
